import humps from "humps";
import lodash, { isEmpty } from "lodash";
import * as R from "ramda";

import {
  FETCH_HRA_RESULT_REQUEST,
  FETCH_HRA_RESULT_SUCCESS,
  FETCH_HRA_RESULT_FAILURE,
  FETCH_HRA_RESULT_DETAIL_REQUEST,
  FETCH_HRA_RESULT_DETAIL_SUCCESS,
  FETCH_HRA_RESULT_DETAIL_FAILURE,
  FETCH_WELLNESS_PRIORITIES_REQUEST,
  FETCH_WELLNESS_PRIORITIES_SUCCESS,
  SET_WELLNESS_PRIORITIES_REQUEST,
  SET_WELLNESS_PRIORITIES_SUCCESS,
  SET_WELLNESS_PRIORITIES_FAILURE,
  SET_UNSUBMITTED_PRIORITIES,
  SELECT_HEALTH_REVIEW_TAB,
} from "../actions/hraResultActions";

const initialHraResultState = {
  isFetching: false,
  fetched: false,
  error: null,
  all: {},
  riskDomainList: [],
  faceAgeingData: {},
  prioritiesEnabled: false,
  hideHraRecommendation: false,
  prioritiesSubmitted: false,
  hraQuestionsAnswered: false,
};

const initialHraResultDetailState = {
  isFetching: false,
  fetched: false,
  error: null,
  riskDomain: null,
  currentRiskDomain: "smoking",
  resultDetail: {},
  selectedTab: 0,
};

const initialWellnessPrioritiesState = {
  isFetching: false,
  fetched: false,
  submitting: false,
  submitted: false,
  error: null,
  priorities: {},
  initialValues: {},
  unsubmittedPriorities: false,
};

// Risk Domain Icons
const riskDomainDir = "/assets/images/wellness";
const SmokingIcon = `${riskDomainDir}/smoking.png`;
const ActivityIcon = `${riskDomainDir}/activity.png`;
const ScreeningIcon = `${riskDomainDir}/screening.png`;
const NutritionIcon = `${riskDomainDir}/nutrition.png`;
const StressIcon = `${riskDomainDir}/stress.png`;
const BodyFatIcon = `${riskDomainDir}/bodyFat.png`;
const PainIcon = `${riskDomainDir}/pain.png`;
const AlcoholIcon = `${riskDomainDir}/alcohol.png`;
const SleepIcon = `${riskDomainDir}/sleep.png`;
const MentalHealthIcon = `${riskDomainDir}/mentalHealth.png`;

const icons = {
  SmokingIcon,
  ActivityIcon,
  ScreeningIcon,
  NutritionIcon,
  StressIcon,
  BodyFatIcon,
  PainIcon,
  AlcoholIcon,
  SleepIcon,
  MentalHealthIcon,
};

const attributes = [
  "Smoking",
  "Activity",
  "Screening",
  "Nutrition",
  "Stress",
  "Body Fat",
  "Pain",
  "Alcohol",
  "Sleep",
  "Mental Health",
];

const parseHraResultData = data => {
  const riskDomainArr = attributes.map(attr => {
    if (data && data[`${humps.camelize(attr)}RiskClass`]) {
      return {
        id: attr.replace(" ", "_").toLowerCase(),
        name: attr,
        riskLevel: data[`${humps.camelize(attr)}RiskClass`],
        icon: icons[`${humps.pascalize(attr)}Icon`],
      };
    }

    return {
      id: attr.replace(" ", "_").toLowerCase(),
      name: attr,
      riskLevel: "",
      icon: "",
    };
  });

  return riskDomainArr;
};

const parseFaceAgeingData = data => {
  if (data) {
    let ageRangeArr = {};
    let healthyPhotosArray = {};
    let unhealthyPhotosArray = {};

    if (!isEmpty(data.ageRange)) {
      ageRangeArr = Array.from(data.ageRange.split("|")).map(Number);
      ageRangeArr.pop();
    }

    if (!isEmpty(data.healthyPhotos)) {
      healthyPhotosArray = Array.from(data.healthyPhotos.split("|"));
      healthyPhotosArray.pop();
    }

    if (!isEmpty(data.unhealthyPhotos)) {
      unhealthyPhotosArray = Array.from(data.unhealthyPhotos.split("|"));
      unhealthyPhotosArray.pop();
    }

    const isFaceAgeingCompleted = data.assessmentStatus.faceAgingCompleted === "true";
    const isAgingPhotoSubmitted = data.assessmentStatus.agingPhotoSubmitted === "true";

    return {
      ageRangeList: ageRangeArr,
      healthyPhotosList: healthyPhotosArray,
      unhealthyPhotosList: unhealthyPhotosArray,
      faceAgingCompleted: isFaceAgeingCompleted,
      agingPhotoSubmitted: isAgingPhotoSubmitted,
    };
  }

  return {};
};

const parseResultDetailData = data => {
  const extractDataAsArray = key => [].concat(lodash.get(data, key)).filter(x => x);

  const outData = {
    riskDetails: "userHealthRiskScore.userHealthRiskDetails.userHealthRiskDetail",
    shopItems: "products.product",
    newShopRecommendation: "shopRecommendation.products",
    quickTips: "userHealthRiskScore.quickTips.quickTip",
    libraryItems: "contents.content",
  };

  // calls extractDataAsArray for each key in outData
  return lodash.mapValues(outData, extractDataAsArray);
};

const parseWellnessPrioritiesIntialValue = fields => {
  const { data } = fields;

  let prioritiesObj = {};

  if (data) {
    data.map(item => {
      prioritiesObj[item.id] = item.value;
    });
  }

  return prioritiesObj;
};

export const hraResult = (state = initialHraResultState, action) => {
  switch (action.type) {
    case FETCH_HRA_RESULT_REQUEST:
      return {
        ...state,
        isFetching: true,
      };

    case FETCH_HRA_RESULT_SUCCESS: {
      return {
        ...state,
        isFetching: false,
        fetched: true,
        all: action.payload,
        riskDomainList: parseHraResultData(action.payload.userHealthRiskScore),
        faceAgeingData: parseFaceAgeingData(action.payload),
        prioritiesEnabled: action.payload.riskFactorPrioritySelection === "true",
        hideHraRecommendation: action.payload.hideHraRecommendation === "true",
        prioritiesSubmitted: false,
        hraQuestionsAnswered: action.payload.assessmentStatus.hraQuestionsAnswered === "true",
      };
    }

    case FETCH_HRA_RESULT_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.payload,
      };

    case SET_WELLNESS_PRIORITIES_SUCCESS:
      return {
        ...state,
        prioritiesSubmitted: true,
      };

    default:
      return state;
  }
};

export const hraResultDetail = (state = initialHraResultDetailState, action) => {
  switch (action.type) {
    case FETCH_HRA_RESULT_DETAIL_REQUEST:
      return {
        ...state,
        isFetching: true,
        currentRiskDomain: action.payload,
      };

    case FETCH_HRA_RESULT_DETAIL_SUCCESS:
      return {
        ...state,
        isFetching: false,
        fetched: true,
        resultDetail: parseResultDetailData(action.payload),
      };

    case FETCH_HRA_RESULT_DETAIL_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.payload,
      };
    case SELECT_HEALTH_REVIEW_TAB:
      return {
        ...state,
        selectedTab: action.payload.selectedTab,
      };
    default:
      return state;
  }
};

export const wellnessPriorities = (state = initialWellnessPrioritiesState, action) => {
  switch (action.type) {
    case FETCH_WELLNESS_PRIORITIES_REQUEST:
      return {
        ...state,
        isFetching: true,
        fetched: false,
      };

    case FETCH_WELLNESS_PRIORITIES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        fetched: true,
        submitting: false,
        submitted: false,
        priorities: action.payload,
        initialValues: parseWellnessPrioritiesIntialValue(action.payload),
      };

    case SET_WELLNESS_PRIORITIES_REQUEST:
      return {
        ...state,
        submitting: true,
        submitted: false,
      };

    case SET_WELLNESS_PRIORITIES_SUCCESS:
      return {
        ...state,
        submitting: false,
        submitted: true,
        priorities: action.payload,
        unsubmittedPriorities: false,
        initialValues: parseWellnessPrioritiesIntialValue(action.payload),
      };

    case SET_WELLNESS_PRIORITIES_FAILURE:
      return {
        ...state,
        error: action.payload,
        submitting: false,
        submitted: false,
      };

    case SET_UNSUBMITTED_PRIORITIES:
      return {
        ...state,
        unsubmittedPriorities: action.payload,
      };

    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./reducers/hraResultReducer.jsx